import { CSSProperties, MouseEventHandler, ReactNode, useMemo } from 'react';

import { Ellipsis, FlexAligner, IconItem } from '@scaleaq/scaleui';
import { DeviceStatus } from '@scaleaq/sitecontrol-lib';
import { capitalize } from 'remeda';

import { StyledCardWrapperWithState } from '@/components/card/styledCardWrapperWithState';
import { Heading, StyledSubtitle, StyledTitle } from '@/components/cardWithThreeFooterBox';
import { SiteNameAndConnectionState } from '@/components/SiteNameAndConnectionState.tsx';
import { Z_INDEX } from '@/constants';
import { useStatusLevelLabels } from '@/hooks';
import { GenericWidgetProps, WidgetHealthIssue } from '@/types.ts';
import { deviceStatusToWidgetHealthIssues, responsive } from '@/utils';

import { HighTrafficAlert } from './gridWidget/HighTrafficAlert.tsx';
import { HeaderComponentSlot, StickyRowWrapper } from './gridWidget/styles.ts';
import { useChartHealthIssues } from './siteNameAndConnectionState/useChartHealthIssues.ts';
import { ToolbarForWidget } from './ToolbarForWidget.tsx';

type GridWidgetProps = GenericWidgetProps & {
  children?: ReactNode;
  disabledToolbar?: boolean;
  headerComponent?: ReactNode;
  headerSubtitle?: string;
  headerTitle?: string;
  hideHeader?: boolean;
  letHeaderFloat?: boolean;
  loadingZIndex?: number;
  onContextMenu?: MouseEventHandler<HTMLDivElement> | undefined;
  /**
   * Cloud-backed widget (OrbitOne data): its header light follows the cloud `/healthz` probe, also
   * on an edge cluster, and the header shows reduced info in portable mode.
   */
  cloudBacked?: boolean;
  toolbarActionList?: IconItem[];

  /** Device-reported status; shown as fallback when `healthIssues` is undefined and the level is not Healthy. */
  status?: DeviceStatus;
  id?: string;
  siteName?: string;
  style?: CSSProperties;

  // ...rest props passed to StyledCardWrapperWithState
  isError?: boolean;
  errorMessage?: string;
  isLoading?: boolean;
  /** The widget's data request is being retried because the API is rate limiting (HTTP 429). */
  isRateLimited?: boolean;
  isEmpty?: boolean;
  emptyMessages?: string[];
  borderRadius?: string;
  /**
   * Explicit issues win over `status` and the loading/error defaults.
   * Pass undefined (not []) to allow that fallback; [] means "no issues" and suppresses it.
   */
  healthIssues?: WidgetHealthIssue[];
};
export const GridWidget = (props: GridWidgetProps) => {
  const {
    children,
    disabledToolbar,
    errorMessage,
    headerComponent,
    headerSubtitle,
    headerTitle,
    healthIssues,
    hideHeader = false,
    id,
    isError,
    isLoading,
    isRateLimited,
    letHeaderFloat = false,
    loadingZIndex = Z_INDEX.overlay.loading,
    cloudBacked = false,
    status,
    style,
    toolbarActionList,
    widgetId,
    ...rest
  } = props;

  const defaultHealthIssues = useChartHealthIssues({
    isLoading: isLoading ?? false,
    isError: isError ?? false,
    isRateLimited,
    fetchFailedMessage: errorMessage,
  });
  // If headerTitle is empty/undefined, show subtitle as title. Ensure both start with uppercase.
  // hideHeader: custom header in children — errorMessage is overlay-only, not the floating title.
  const displayTitle = capitalize(
    headerTitle || headerSubtitle || (hideHeader ? '' : errorMessage) || ''
  );
  const displaySubtitle = headerTitle ? capitalize(headerSubtitle || '') : undefined;
  const hasTitleOrMessage = !!displayTitle;

  const statusLevelLabels = useStatusLevelLabels();

  const statusHealthIssues = useMemo(
    () => deviceStatusToWidgetHealthIssues(status, status && statusLevelLabels[status.level]),
    [status, statusLevelLabels]
  );

  const resolvedHealthIssues = healthIssues ?? statusHealthIssues ?? defaultHealthIssues;

  return (
    <StyledCardWrapperWithState
      {...rest}
      isLoading={isLoading}
      isError={isError}
      errorMessage={errorMessage}
      loadingZIndex={loadingZIndex}
      gap={'0'}
      className={'keep-background'}
      style={{ ...style }}
    >
      <StickyRowWrapper $isHidden={hideHeader} $float={letHeaderFloat}>
        {hasTitleOrMessage && (
          <FlexAligner fullHeight direction='column' alignment='bottom-right'>
            <FlexAligner
              fullHeight
              alignment='bottom-left'
              style={{
                width: letHeaderFloat ? 'fit-content' : '100%',
                maxWidth: letHeaderFloat ? '19rem' : '100%',
              }}
            >
              <Heading
                alignment={hasTitleOrMessage ? 'center' : 'center-right'}
                spaceBetween={hasTitleOrMessage}
                gap={letHeaderFloat ? responsive.spacing['5xlarge'] : responsive.spacing['large']}
              >
                <FlexAligner direction='column' gap={responsive.spacing['small']}>
                  <Ellipsis>
                    <StyledTitle type='heading_large' $titleZIndex={loadingZIndex + 5}>
                      {displayTitle}
                    </StyledTitle>
                  </Ellipsis>
                  {displaySubtitle && (
                    <StyledSubtitle type='body_small' $titleZIndex={loadingZIndex + 5}>
                      {displaySubtitle}
                    </StyledSubtitle>
                  )}
                </FlexAligner>
                {headerComponent && <HeaderComponentSlot>{headerComponent}</HeaderComponentSlot>}
                <SiteNameAndConnectionState
                  cloudBacked={cloudBacked}
                  zIndex={loadingZIndex + 2}
                  healthIssues={resolvedHealthIssues}
                >
                  <ToolbarForWidget
                    widgetId={widgetId || id}
                    toolbarActionList={toolbarActionList}
                    disabled={disabledToolbar}
                    zIndex={Z_INDEX.content.sticky}
                  />
                </SiteNameAndConnectionState>
              </Heading>
            </FlexAligner>
          </FlexAligner>
        )}
      </StickyRowWrapper>
      {isRateLimited && <HighTrafficAlert zIndex={loadingZIndex + 5} />}
      {children}
    </StyledCardWrapperWithState>
  );
};
